import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ExternalLink,
  Filter as FilterIcon,
  Maximize2,
  Plus as PlusIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  X as XIcon,
} from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import type { BrandInsightsPageContext, Chat } from '@trinity/chat';
import { BiChatContextType, ChatContextType } from '@trinity/chat';
import {
  AuraChatContext,
  BrandInsightsModalContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat';
import {
  ActiveChatSessionContext,
  AuthorContext,
  ChatPageContextDataContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { calculateActiveFiltersCount } from '@trinity/chat-components/components/Chat.Filters/common';
import { FilterPaneWorkspace } from '@trinity/chat-components/components/Chat.Filters/FilterPane.Workspace';
import { useHeaderContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ContextSearch } from '@trinity/chat-components/components/Chat.Header/ContextSearch';
import { ChatContextSearchContainer } from '@trinity/chat-components/components/Chat.Header/ContextSearch.Container';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container';
import { SimpleContextSelector } from '@trinity/chat-components/components/Chat.Header/SimpleContextSelector';
import {
  ChatHistory,
  ChatHistoryItemsContext,
  ChatHistorySharedContext,
} from '@trinity/chat-components/components/Chat.History/index.js';
import {
  SidePanelAnimatedContext,
  SidePanelToggleContext,
} from '@trinity/chat-components/components/Chat.SidePanel/context';
import {
  getSentimentDefaultPanelWidth,
  MODAL_FALLBACK_DRAG_BUFFER,
  PANEL_SIZING,
  SIDE_PANEL_MIN_CONTAINER_WIDTH,
  SIDE_PANEL_TRANSITION_MS,
} from '@trinity/chat-components/components/Chat.SidePanel/sizing';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { RateLimitingContext } from '@trinity/chat-components/containers/RateLimiting/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';

// ─── Sparkle SVG (Figma: #565ADD) ────────────────────────────────────────────
const SparkleIcon = () => <AutoAwesomeOutlinedIcon style={{ width: 18, height: 18, color: '#565ADD' }} />;

// ─── Hamburger SVG — three bars of equal width (Figma: #565ADD) ─────────
const HamburgerSVG = () => (
  <svg width="21" height="14" viewBox="0 0 21 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="21" height="2" rx="1" fill="#565ADD" />
    <rect y="6" width="21" height="2" rx="1" fill="#565ADD" />
    <rect y="12" width="21" height="2" rx="1" fill="#565ADD" />
  </svg>
);

// ─── Settings sub-panel ───────────────────────────────────────────────────────
const SidePanelSettingsView = () => {
  const user = useContext(AuthorContext);
  const { query } = useContext(RateLimitingContext);

  const stats = useMemo(() => {
    const rawLimit = query?.data?.limit;
    const limit = rawLimit === 0 ? 5000 : (rawLimit ?? null);
    return { current: query?.data?.current ?? 0, limit };
  }, [query?.data]);

  const limitDisplay = stats.limit === null ? 'No Preset Limit' : stats.limit.toLocaleString();

  return (
    <FullPanelView>
      <SettingsContent>
        <SettingsTitle>Chat Settings</SettingsTitle>
        <SettingsRow>{`Username: ${user?.profile?.email ?? '—'}`}</SettingsRow>
        <SettingsRow>{`Submitted Chats: ${stats.current.toLocaleString()} / ${limitDisplay}`}</SettingsRow>
      </SettingsContent>
    </FullPanelView>
  );
};

// ─── History sub-panel ────────────────────────────────────────────────────────
type HistoryViewProps = {
  items: Chat.Instance[];
  shared: Chat.Instance[];
  onNewChat: () => void;
  onSettings: () => void;
  isRateLimitExceeded: boolean;
};

const SidePanelHistoryView = ({
  items,
  shared,
  onNewChat,
  onSettings,
  isRateLimitExceeded,
}: HistoryViewProps) => (
  <FullPanelView>
    <HistoryNewChatRow>
      <NewChatPill disabled={isRateLimitExceeded} onClick={onNewChat}>
        <PlusIcon size={14} />
        New Chat
      </NewChatPill>
    </HistoryNewChatRow>
    <HistoryScrollArea>
      <ChatHistory items={items} shared={shared} />
    </HistoryScrollArea>
    <HistoryFooter>
      <SettingsGearButton onClick={onSettings} title="Chat Settings" aria-label="Chat Settings">
        <SettingsIcon size={20} />
      </SettingsGearButton>
    </HistoryFooter>
  </FullPanelView>
);

// Width thresholds live in ./sizing so Layout.tsx can share them without an import cycle.

const clampPanelWidth = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

type PanelWidthBounds = { min: number; max: number; canHostPanel: boolean };

const getPanelWidthBoundsForContainer = (containerWidth: number): PanelWidthBounds => {
  const minVisibleMainContentWidth = containerWidth * PANEL_SIZING.minVisibleMainContentRatio;
  const maxWidthFromMainAreaFloor = containerWidth - minVisibleMainContentWidth;
  const maxWidthFromPanelCeiling = containerWidth * PANEL_SIZING.maxWidthRatio;
  const maxPanelWidth = Math.min(maxWidthFromMainAreaFloor, maxWidthFromPanelCeiling);

  return {
    min: PANEL_SIZING.minWidth,
    max: Math.max(PANEL_SIZING.minWidth, maxPanelWidth),
    canHostPanel: containerWidth >= SIDE_PANEL_MIN_CONTAINER_WIDTH,
  };
};

const getInitialPanelWidth = (
  containerWidth: number,
  responsiveWidthMode: ChatSidePanelProps['responsiveWidthMode']
): number => {
  const { min, max } = getPanelWidthBoundsForContainer(containerWidth);
  const preferredWidth =
    responsiveWidthMode === 'sentiment'
      ? getSentimentDefaultPanelWidth(containerWidth)
      : containerWidth * PANEL_SIZING.defaultWidthRatio;
  return clampPanelWidth(preferredWidth, min, max);
};

type ChatSidePanelProps = {
  hideExpandButton?: boolean;
  responsiveWidthMode?: 'legacy' | 'sentiment';
};

// ─── Main component ───────────────────────────────────────────────────────────
export const ChatSidePanel = ({
  hideExpandButton = false,
  responsiveWidthMode = 'legacy',
}: ChatSidePanelProps) => {
  const pageContext = useContext(ChatPageContextDataContext);
  const chat = useContext(ActiveChatSessionContext);
  const [isExpanded, setIsExpanded] = useContext(SidePanelExpandedContext);
  const isAnimatedContainer = useContext(SidePanelAnimatedContext);
  const { closePanel, isOpen } = useContext(SidePanelToggleContext);
  const [, toggleModal] = useContext(BrandInsightsModalContext);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const previousChatIdentifierRef = useRef<string | null>(chat?.identifier ?? null);
  const items = useContext(ChatHistoryItemsContext);
  const shared = useContext(ChatHistorySharedContext);

  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const chatWelcomeScreen = useContext(ChatWelcomeScreenContext);
  const { query } = useContext(RateLimitingContext);
  const { setAuraChatStatus } = useContext(AuraChatContext);
  const [width, setWidth] = useState(() => {
    const containerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return getInitialPanelWidth(containerWidth, responsiveWidthMode);
  });
  const wrapRef = useRef<HTMLDivElement>(null);

  const filters = useContext(ChatSessionFiltersContext);
  const features = useContext(GroupFeaturesContext);
  const resolvedFeatures = useMemo(
    () => ({
      hcpProfiles: features?.hcpProfiles ?? false,
      workspaces: features?.workspaces ?? false,
      engagementFilter: features?.engagementFilter ?? false,
    }),
    [features?.hcpProfiles, features?.workspaces, features?.engagementFilter]
  );

  const activeFiltersCount = useMemo(() => {
    const currentContext = chat?.context ?? pageContext;
    const baseCount = calculateActiveFiltersCount({
      context: currentContext,
      data: filters,
      features: resolvedFeatures,
    });

    if (!currentContext) {
      return baseCount;
    }

    const hasProjectInFilters = filters.projects.length > 0;
    const hasContextProject =
      currentContext.type === ChatContextType.Project ||
      (currentContext.type === ChatContextType.Agent &&
        currentContext.data.identifier === BiChatContextType.Project);

    if (!hasProjectInFilters && hasContextProject) {
      return baseCount + 1;
    }

    return baseCount;
  }, [chat?.context, pageContext, filters, resolvedFeatures]);

  const handleClosePanel = useCallback(() => {
    closePanel();
    setIsExpanded(false);
  }, [closePanel, setIsExpanded]);

  // Latch so the drag handler and the container-resize watcher can't both hand off to the
  // modal for the same transition (a double `toggleModal()` would close it immediately).
  const hasRequestedFallbackRef = useRef(false);

  const requestModalFallback = useCallback(() => {
    // No modal to fall back to here: contexts that hide the Expand button (e.g. GenAI's
    // always-on side panel) have no expand-to-modal affordance, so escalating to the modal
    // would only stack it on top of a panel that's already on screen.
    if (hideExpandButton) return;
    if (hasRequestedFallbackRef.current) return;
    hasRequestedFallbackRef.current = true;
    closePanel();
    toggleModal(true);
  }, [closePanel, toggleModal, hideExpandButton]);

  // Reset the latch whenever the panel is opened again, so a later transition can fire.
  useEffect(() => {
    if (isOpen) {
      hasRequestedFallbackRef.current = false;
    }
  }, [isOpen]);

  const handleExpandClick = useCallback(() => {
    requestModalFallback();
  }, [requestModalFallback]);

  const handleNewChat = useCallback(() => {
    setAuraChatStatus(false);
    chatWelcomeScreen?.dismiss();
    setShowHistory(false);
    setShowSettings(false);
    setShowFilters(false);

    const effectiveContext = pageContext ?? chat?.context;
    if (!effectiveContext) return;

    const resetLandingCards =
      effectiveContext.type === ChatContextType.Agent && effectiveContext.data.showLandingCardsOnNewChat;

    if (!brandIdentifier) {
      console.warn('Brand identifier is required to start a new chat');
      return;
    }

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(effectiveContext, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [setAuraChatStatus, chatWelcomeScreen, dispatch, pageContext, chat?.context, brandIdentifier]);

  const resizeCleanupRef = useRef<(() => void) | null>(null);

  const getPanelWidthBounds = useCallback(() => {
    const containerWidth =
      wrapRef.current?.closest('[data-panel-layout-container]')?.getBoundingClientRect().width ??
      window.innerWidth;
    // In in-flow layout mode, keep part of the main area visible at all times.
    return getPanelWidthBoundsForContainer(containerWidth);
  }, []);

  // Auto-set context to the current page when the panel transitions closed → open.
  // This ensures the chat context matches the page (e.g. opening the panel on a Project
  // page starts a project-scoped chat) without disrupting chats that already match.
  const wasExpandedRef = useRef(false);
  useEffect(() => {
    const justOpened = isExpanded && !wasExpandedRef.current;
    wasExpandedRef.current = isExpanded;

    if (!justOpened || !pageContext || !brandIdentifier) return;
    if (pageContext.type === ChatContextType.Global) return;
    if (chat?.context?.type === pageContext.type) return;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(pageContext, false),
        brandIdentifier,
        resetLandingCards: false,
      },
    });
  }, [isExpanded, pageContext, brandIdentifier, chat?.context?.type, dispatch]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const clampCurrentWidth = () => {
      // Compute bounds outside the `setWidth` updater: escalating to the modal dispatches
      // into other components, which must never happen during a render-phase update.
      const { min, max, canHostPanel } = getPanelWidthBounds();

      if (!canHostPanel) {
        // No modal fallback available (see requestModalFallback) — just clamp instead of
        // escalating to a modal that would stack on top of a panel that's already on screen.
        if (hideExpandButton) {
          setWidth(min);
          return;
        }
        requestModalFallback();
        return;
      }

      setWidth((currentWidth) => Math.max(min, Math.min(max, currentWidth)));
    };

    clampCurrentWidth();
    window.addEventListener('resize', clampCurrentWidth);
    return () => window.removeEventListener('resize', clampCurrentWidth);
  }, [getPanelWidthBounds, isExpanded, requestModalFallback, hideExpandButton]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = wrapRef.current?.offsetWidth || 0;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const diff = startX - moveEvent.clientX;
        const nextWidth = startWidth + diff;
        const { min, max } = getPanelWidthBounds();

        // Dragged narrower than the composer can tolerate — hand the session to the modal
        // and end the drag rather than clamping the user against an invisible wall.
        if (nextWidth < min - MODAL_FALLBACK_DRAG_BUFFER) {
          // No modal fallback available here (see requestModalFallback) — clamp at the
          // minimum and keep the drag alive instead of escalating to the modal.
          if (hideExpandButton) {
            setWidth(min);
            return;
          }
          resizeCleanupRef.current?.();
          resizeCleanupRef.current = null;
          requestModalFallback();
          return;
        }

        // Allow travel into the buffer zone so the drag-past gesture is discoverable.
        setWidth(Math.max(min - MODAL_FALLBACK_DRAG_BUFFER, Math.min(max, nextWidth)));
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        resizeCleanupRef.current = null;
        // Released inside the buffer zone without crossing the threshold — snap back to
        // the minimum so the panel never rests narrower than the composer allows.
        setWidth((currentWidth) => Math.max(PANEL_SIZING.minWidth, currentWidth));
      };

      resizeCleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [getPanelWidthBounds, requestModalFallback, hideExpandButton]
  );

  const isRateLimitExceeded = useMemo(() => query?.data?.allowed === false, [query?.data?.allowed]);

  useEffect(() => {
    const currentChatIdentifier = chat?.identifier ?? null;
    const previousChatIdentifier = previousChatIdentifierRef.current;
    const hasChatSelectionChanged =
      previousChatIdentifier !== null &&
      currentChatIdentifier !== null &&
      previousChatIdentifier !== currentChatIdentifier;

    if (hasChatSelectionChanged && showHistory) {
      setShowHistory(false);
      setShowSettings(false);
    }

    previousChatIdentifierRef.current = currentChatIdentifier;
  }, [chat?.identifier, showHistory]);

  // Exit animation: inside an animated container, closing the panel collapses that
  // container's width over SIDE_PANEL_TRANSITION_MS (see SidePanelArea in Layout.tsx).
  // Stay mounted for that window so the content slides out with it instead of blinking out
  // of existence while the main content is still expanding into the space.
  const [isExiting, setIsExiting] = useState(false);
  const wasExpandedForExitRef = useRef(isExpanded);
  // True only on the render in which `isExpanded` flipped to false — the effect below
  // hasn't run yet, so without this the panel would already have returned null.
  const isStartingExit = isAnimatedContainer && !isExpanded && wasExpandedForExitRef.current;

  useEffect(() => {
    const wasExpanded = wasExpandedForExitRef.current;
    wasExpandedForExitRef.current = isExpanded;

    if (!isAnimatedContainer || isExpanded || !wasExpanded) {
      setIsExiting(false);
      return undefined;
    }

    setIsExiting(true);
    const timeoutId = window.setTimeout(() => setIsExiting(false), SIDE_PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isAnimatedContainer, isExpanded]);

  if (!isExpanded && !isExiting && !isStartingExit) return null;

  const inSubView = showHistory || showSettings;

  return (
    <Root>
      <ResizeHandle onMouseDown={handleResizeStart} />
      <Wrap customWidth={width} ref={wrapRef}>
        {/* ── Header (65px, always visible) ── */}
        <Header>
          <Menubar>
            <HamburgerButton
              onClick={() => {
                setShowFilters(false);
                if (showSettings) {
                  setShowSettings(false);
                  setShowHistory(true);
                  return;
                }
                if (showHistory) {
                  setShowHistory(false);
                  setShowSettings(false);
                  return;
                }
                setShowHistory(true);
              }}
              title={inSubView ? 'Back to chat' : 'Chat history'}
              aria-label={inSubView ? 'Back to chat' : 'Chat history'}
            >
              {inSubView ? (
                <BackArrowLabel>
                  <ChevronLeft size={14} strokeWidth={2.5} />
                  Back
                </BackArrowLabel>
              ) : (
                <HamburgerSVG />
              )}
            </HamburgerButton>

            <TitleGroup>
              <SparkleIcon />
              <TitleText>Insights AI</TitleText>
            </TitleGroup>

            <HeaderIcons>
              {/* NorthEast: hidden per PRD §18.2 (future "open in new window") */}
              <FlatIconButton disabled aria-label="Open in new window" style={{ visibility: 'hidden' }}>
                <ExternalLink size={14} />
              </FlatIconButton>
              {!hideExpandButton && (
                <FlatIconButton onClick={handleExpandClick} aria-label="Expand to modal">
                  <Maximize2 size={28} />
                </FlatIconButton>
              )}
              <FlatCloseButton onClick={handleClosePanel} aria-label="Close panel">
                <XIcon size={28} />
              </FlatCloseButton>
            </HeaderIcons>
          </Menubar>
          <HeaderDivider />
        </Header>

        {/* ── Content area ── */}
        {renderContent()}
      </Wrap>
    </Root>
  );

  function renderContent() {
    if (showSettings) {
      return <SidePanelSettingsView />;
    }
    if (showHistory) {
      return (
        <SidePanelHistoryView
          items={items}
          shared={shared}
          onNewChat={handleNewChat}
          onSettings={() => setShowSettings(true)}
          isRateLimitExceeded={isRateLimitExceeded}
        />
      );
    }
    return (
      <MainContent>
        {/* ── Topic container (context selector + filter + new chat) ── */}
        <TopicWrapper>
          <ContextSelectorContainer>
            <SidePanelTopicArea
              activeFiltersCount={activeFiltersCount}
              isRateLimitExceeded={isRateLimitExceeded}
              onFilterClick={() => setShowFilters((prev) => !prev)}
              onNewChat={handleNewChat}
              pageContext={pageContext}
            />
          </ContextSelectorContainer>
        </TopicWrapper>
        {/* Chat area: position:relative so the filter overlay anchors to it */}
        <ChatArea>
          {showFilters && (
            <SidePanelFilterView active={activeFiltersCount > 0} onClose={() => setShowFilters(false)} />
          )}
          <ChatScrollContainer>
            <ChatInstance isSidePanel={true} />
          </ChatScrollContainer>
        </ChatArea>
      </MainContent>
    );
  }
};

ChatSidePanel.displayName = 'Chat.SidePanel';

// ─── SidePanelTopicArea ──────────────────────────────────────────────────────
// Rendered inside ContextSelectorContainer so useHeaderContext() is available.

type SidePanelTopicAreaProps = {
  activeFiltersCount: number;
  isRateLimitExceeded: boolean;
  onFilterClick: () => void;
  onNewChat: () => void;
  pageContext: BrandInsightsPageContext.Item;
};

function SidePanelTopicArea({
  activeFiltersCount,
  isRateLimitExceeded,
  onFilterClick,
  onNewChat,
  pageContext,
}: SidePanelTopicAreaProps) {
  const { setPendingContextType, setPendingEverythingSearch, pendingContextType, pendingEverythingSearch } =
    useHeaderContext();

  const hasActiveSearch = !!(pendingContextType ?? pendingEverythingSearch);

  const handleSearchClick = useCallback(() => {
    if (!pageContext || pageContext.type === ChatContextType.Global) {
      setPendingEverythingSearch(true);
      return;
    }

    if (pageContext.type === ChatContextType.Agent) {
      // Map BiChatContextType identifiers to the corresponding ChatContextType so the
      // ContextSearch placeholder shows e.g. "Search projects" rather than "Search Anything".
      const biTypeToContextType: Partial<Record<string, ChatContextType>> = {
        [BiChatContextType.Project]: ChatContextType.Project,
        [BiChatContextType.Brand]: ChatContextType.Brand,
        [BiChatContextType.Transcript]: ChatContextType.Transcript,
        [BiChatContextType.Document]: ChatContextType.Document,
        [BiChatContextType.Folder]: ChatContextType.Folder,
      };
      const mapped = biTypeToContextType[pageContext.data.identifier];
      if (mapped) {
        setPendingContextType(mapped);
      } else {
        setPendingEverythingSearch(true);
      }
      return;
    }

    setPendingContextType(pageContext.type);
  }, [pageContext, setPendingContextType, setPendingEverythingSearch]);

  return (
    <TopicRow>
      <TopicInner>
        <ChatContextSearchContainer>
          {hasActiveSearch ? (
            <StyledContextSearch />
          ) : (
            <>
              <ContextSelectorWrapper>
                <SimpleContextSelector />
              </ContextSelectorWrapper>
              <TopicIconButton onClick={handleSearchClick} aria-label="Search context">
                <SearchIcon size={17} color="#565ADD" />
              </TopicIconButton>
            </>
          )}
        </ChatContextSearchContainer>
        <SidePanelFilterButton
          count={activeFiltersCount}
          onClick={onFilterClick}
          aria-label="Source filters"
        />
      </TopicInner>
      <NewChatCircle disabled={isRateLimitExceeded} onClick={onNewChat} aria-label="New chat">
        <StyledPlusIcon />
      </NewChatCircle>
    </TopicRow>
  );
}

// ─── Filter overlay ───────────────────────────────────────────────────────────
type FilterViewProps = {
  active: boolean;
  onClose: () => void;
};

const SidePanelFilterView = ({ active, onClose }: FilterViewProps) => {
  const dispatch = useContext(ChatStateDispatchContext);

  const handleClickAway = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    dispatch({ type: 'filters/reset' });
  }, [dispatch]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FilterOverlay>
        <FilterViewHeader>
          <FilterViewTitle>Source Filters</FilterViewTitle>
          <FilterResetButton disabled={!active} onClick={handleReset}>
            Reset
          </FilterResetButton>
        </FilterViewHeader>
        <FilterPaneWorkspace active={active} isSidePanel={true} />
      </FilterOverlay>
    </ClickAwayListener>
  );
};

// ─── Styled components (object syntax per AGENTS.md) ─────────────────────────

const Root = styled('div')({
  boxSizing: 'border-box',
  height: '100%',
  position: 'relative',
  display: 'flex',
});

const Wrap = styled('div')<{ customWidth: number }>(({ customWidth, theme }) => ({
  position: 'relative',
  boxSizing: 'border-box',
  width: `${customWidth}px`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#FFFFFF',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderLeft: `1px solid ${theme.palette.divider}`,
  paddingTop: 6,
  boxShadow: 'none',
  borderRadius: 0,
  overflow: 'hidden',
  overscrollBehavior: 'contain',
}));

const ResizeHandle = styled('div')({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 4,
  height: '100%',
  cursor: 'col-resize',
  background: 'transparent',
  zIndex: 100,
  '&:hover': { background: '#0078d4' },
});

// ── Header ───────────────────────────────────────────────────────────────────

const Header = styled('div')({
  width: '100%',
  flexShrink: 0,
});

const Menubar = styled('div')({
  height: 65,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 14px',
  background: '#FFFFFF',
  boxSizing: 'border-box',
});

const HamburgerButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  borderRadius: 4,
  '&:hover': { background: '#F5F5F5' },
});

const TitleGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flex: 1,
  justifyContent: 'center',
});

const TitleText = styled('span')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  color: '#121217',
  userSelect: 'none',
}));

// Shown in place of the hamburger icon when in a sub-view (history/settings)
const BackArrowLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  color: theme.palette.sentiment.primary.main,
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
}));

const HeaderIcons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
});

const FlatIconButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  color: '#18181B',
  boxSizing: 'border-box',
  width: 28,
  height: 28,
  '&:hover:not(:disabled)': { background: '#F5F5F5' },
  '&:disabled': { cursor: 'default' },
});

const FlatCloseButton = styled(FlatIconButton)({
  border: 'none',
  borderRadius: 3,
  padding: 2,
});

const HeaderDivider = styled('div')({
  height: 1,
  background: '#E6E6E6',
  margin: '0 14px',
});

// ── Topic container ───────────────────────────────────────────────────────────

type SidePanelFilterButtonProps = {
  count: number;
  onClick: () => void;
  'aria-label'?: string;
};

const SidePanelFilterButton = ({ count, onClick, 'aria-label': ariaLabel }: SidePanelFilterButtonProps) => {
  const isActive = count > 0;
  return (
    <FilterSquareButton onClick={onClick} aria-label={ariaLabel}>
      <FilterIconWrap>
        <FilterIcon size={16} color="#FFFFFF" />
        {isActive && <FilterActiveBadge>{count}</FilterActiveBadge>}
      </FilterIconWrap>
    </FilterSquareButton>
  );
};

const MainContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

// ChatArea wraps ChatInstance and the filter overlay; position:relative is required
// so the absolutely-positioned FilterOverlay is anchored inside it.
// overflow: visible allows dropdowns to escape the container bounds without clipping.
// minHeight: 0 is required alongside that: a flex item with overflow:visible and no
// min-height override gets an automatic minimum size equal to its content's natural
// height (i.e. the full message list), so without this ChatArea refuses to shrink to
// its allotted flex space, grows past MainContent's clipped (overflow:hidden) box, and
// the footer/input — further down inside this subtree — ends up clipped off and
// unreachable as the message list grows.
const ChatArea = styled('div')({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
});

// ChatScrollContainer sizes the area ChatInstance renders into; ChatArea remains the
// position anchor for FilterOverlay and allows dropdown overflow. Scrolling itself is
// handled internally by ChatInstance (its Messages area scrolls, its Footer/input stays
// pinned) — this container must NOT also be a scrollable flex column, or ChatInstanceRoot
// (a flex item here with default overflow:visible min-height behavior) gets sized to fit
// all message content and the whole header+messages+footer block scrolls together,
// pushing the input out of reach.
const ChatScrollContainer = styled('div')({
  flex: 1,
  overflow: 'hidden',
  minWidth: 0,
  minHeight: 0,
});

const TopicWrapper = styled('div')({
  padding: '10px 14px',
  flexShrink: 0,
});

// TopicRow holds the bordered context box (flex:1) and the New Chat circle (outside box)
const TopicRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const TopicInner = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid #E8E8EC',
  borderRadius: 8,
  padding: '0 12px',
  height: 46,
  gap: 8,
  flex: 1,
  minWidth: 0,
});

// Wraps SimpleContextSelector to allow it to flex-grow inside TopicInner.
// SimpleContextSelector does not forward className, so a wrapper div is used.
const ContextSelectorWrapper = styled('div')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
});

const StyledContextSearch = styled(ContextSearch)({
  flex: 1,
  minWidth: 0,
});

const TopicIconButton = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 4,
  '&:hover': { background: '#F0EFFC' },
});

const FilterSquareButton = styled('button')(() => ({
  background: '#565ADD',
  border: 'none',
  borderRadius: 7,
  width: 30,
  height: 30,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background 0.15s',
  outline: 'none',
  '&:hover': { background: '#4340a8' },
}));

const FilterIconWrap = styled('span')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

const FilterActiveBadge = styled('span')({
  position: 'absolute',
  top: -5,
  right: -5,
  minWidth: 14,
  height: 14,
  borderRadius: 7,
  backgroundColor: '#00B4AD',
  color: '#FFFFFF',
  fontSize: 9,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  padding: '0 2px',
  boxSizing: 'border-box',
});

const NewChatCircle = styled('button')({
  borderRadius: '50%',
  backgroundColor: '#4845B8',
  width: 30,
  height: 30,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  flexShrink: 0,
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    cursor: 'not-allowed',
  },
});

const StyledPlusIcon = styled(PlusIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  strokeWidth: 3,
  width: 18,
  minWidth: 18,
}));

// ── History panel ─────────────────────────────────────────────────────────────

const FullPanelView = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
});

const HistoryNewChatRow = styled('div')({
  padding: '16px 14px 18px',
  flexShrink: 0,
  borderBottom: '1px solid #E8E8EC',
});

const NewChatPill = styled('button')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  backgroundColor: '#4845B8',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 20,
  padding: '10px 16px',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    cursor: 'not-allowed',
  },
});

const HistoryScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
});

const HistoryFooter = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '10px 14px 16px',
  borderTop: '1px solid #E8E8EC',
  flexShrink: 0,
});

const SettingsGearButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: theme.palette.gray.main ?? '#999999',
  padding: 4,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  '&:hover': { color: theme.palette.sentiment.primary.main },
}));

// ── Settings panel ────────────────────────────────────────────────────────────

// ── Filter overlay panel ──────────────────────────────────────────────────────

// Absolutely positioned over the chat area, anchored to top of ChatArea.
// Height grows with content so the chat input bar stays visible below.
const FilterOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  background: '#FFFFFF',
  zIndex: 10,
  padding: '12px 14px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  overflow: 'visible',
});

const FilterViewHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  flexShrink: 0,
});

const FilterViewTitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  color: '#000000',
}));

const FilterResetButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  background: 'none',
  border: 'none',
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? '#C9C9C9' : '#A9A9A9',
  fontFamily: 'inherit',
  fontSize: 16,
  padding: '4px 0',
  flexShrink: 0,
  ...(!disabled && {
    '&:hover': {
      color: '#666666',
    },
    '&:active': {
      color: '#444444',
    },
    '&:focus-visible': {
      outline: '2px solid #A9A9A9',
      outlineOffset: 2,
      borderRadius: 2,
    },
  }),
}));

const SettingsContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '24px 20px',
});

const SettingsTitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 20,
  color: '#000000',
}));

const SettingsRow = styled('div')({
  fontSize: 16,
  color: '#000000',
});
